import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Grid, Stack, SvgIcon } from "@mui/material";
import { ChevronRight, Done, ExpandMore } from "@mui/icons-material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import { useClipboard } from "use-clipboard-copy";
import { enqueueSnackbar } from "notistack";
import { useAccount } from "wagmi";

import { StyledEmptyWrapper } from "@framework/styled";
import { IReferralTree } from "@framework/types";
import { AddressLink } from "@dzambalaorg/mui-scanner";
import { Breadcrumbs, PageHeader, ProgressOverlay } from "@dzambalaorg/mui-page-layout";
import { useFetchQuery } from "@dzambalaorg/react-hooks";
import { IPaginationResult } from "@dzambalaorg/types-collection";
import { Button, TextField, Typography } from "@dzambalaorg/ui-kit";

import {
  calculateDepth,
  emptyRefProgram,
  getRefLevelShare,
  type IRefProgramsLevels,
} from "../../../../../utils/referral";
import { ReferralTreeMerchantSearchForm } from "./search";

const marketUrl = process.env.MARKET_FE_URL;

export const ReferralTree = () => {
  const { isLoading, allRows } = useFetchQuery<IPaginationResult<IReferralTree>>("/erc721/contracts", void 0, void 0, {
    merchantIds: [],
  });

  const clipboard = useClipboard();
  const { formatMessage } = useIntl();

  const { address: account = "" } = useAccount();

  const [copied, setCopied] = useState<boolean>(false);
  const [programs, setPrograms] = useState<Array<IRefProgramsLevels>>([emptyRefProgram]);

  const refLink = `${marketUrl}/?referrer=${account.toLowerCase()}`;

  const handleCopy = () => {
    clipboard.copy();
    enqueueSnackbar(formatMessage({ id: "snackbar.clipboard" }));
    setCopied(true);
    setTimeout(() => {
      setCopied(false);
    }, 1000);
  };

  useEffect(() => {
    if (allRows?.length) {
      setPrograms(
        allRows.map((row: IReferralTree) => ({
          merchantId: row.merchantId,
          levels: row.merchant.refLevels!.map(lev => ({ level: lev.level, share: lev.share })),
        })),
      );
    }
  }, [allRows]);

  const renderTreeLabel = (node: IReferralTree, idx: number) => {
    const { id, wallet, merchant, level /* share */ } = node;
    const depth = calculateDepth(allRows[idx], id);
    const level0 = getRefLevelShare(programs, merchant.id, 0);
    const totalShares = level0.share / 100;
    const { share, totalCount } = getRefLevelShare(programs, merchant.id, depth || level);
    const shareLabel =
      wallet === account.toLowerCase() // SELF
        ? `Total referral program share: ${totalShares}% on ${totalCount} levels`
        : `Your share from level ${depth || level} is ${share ? share / 100 : 0}%`;

    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 0.5,
          pr: 0,
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: "inherit", flexGrow: 1 }}>
          {wallet === account.toLowerCase() ? merchant.title : <AddressLink address={wallet} />}
        </Typography>
        {share ? (
          <Typography variant="caption" color="inherit" sx={{ fontWeight: "inherit" }}>
            {shareLabel}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const renderTree = (row: IReferralTree, idx: number) => (
    <TreeItem key={row.id} itemId={row.id.toString()} label={renderTreeLabel(row, idx)}>
      {row.children.length > 0 ? row.children.map(items => renderTree(items, idx)) : null}
    </TreeItem>
  );

  return (
    <Grid>
      <Breadcrumbs path={["dashboard", "referral", "referral.tree"]} />

      <PageHeader message="pages.referral.tree.title" />

      <ReferralTreeMerchantSearchForm initialValues={{ merchantIds: [] }} />

      <Grid container spacing={4} alignItems="center">
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack mb={2}>
            <TextField
              value={`${refLink}`}
              label={formatMessage({ id: "pages.referral.tree.refLink" })}
              inputRef={clipboard.target}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: (
                    <Button onClick={handleCopy}>
                      {!copied ? (
                        <FormattedMessage id="form.buttons.copy" />
                      ) : (
                        <SvgIcon component={Done} width={32} height={32} />
                      )}
                    </Button>
                  ),
                },
              }}
            />
          </Stack>
        </Grid>
      </Grid>

      <ProgressOverlay isLoading={isLoading}>
        <StyledEmptyWrapper count={allRows.length} isLoading={isLoading}>
          <Box sx={{ minHeight: 150, flexGrow: 1, mt: 3 }}>
            <SimpleTreeView
              aria-label="referral tree"
              defaultExpandedItems={["root"]}
              slots={{ expandIcon: ChevronRight, collapseIcon: ExpandMore }}
            >
              {allRows.map((row, idx) => renderTree(row, idx))}
            </SimpleTreeView>
          </Box>
        </StyledEmptyWrapper>
      </ProgressOverlay>
    </Grid>
  );
};
